import { CANONICAL_CRS_IDENTIFIER, type CrsMetadata } from '../../crs/model';
import type { SourceWarning, VectorFeatureCollection } from '../model';
import { parseGeoJsonCrsDeclaration, type ParsedGeoJsonCrs } from './geoJsonCrs';
import { inspectGeoJson, type GeoJsonInspection } from './geojsonInspection';
import { warningsForInspection } from './importWarnings';
import type { ImportedVectorData } from './model';
import {
  parseCanonicalVectorData,
  type CoordinateTransform,
  VectorFeatureValidationError,
} from './vectorFeatureValidation';

export type GeoJsonImportErrorCode =
  | 'file-read'
  | 'invalid-json'
  | 'not-geojson'
  | 'malformed-geojson'
  | 'invalid-coordinates'
  | 'file-too-large'
  | 'unsupported-crs'
  | 'unsupported-file-type'
  | 'unsupported-geometry';

export interface GeoJsonImportError {
  readonly code: GeoJsonImportErrorCode;
  readonly message: string;
}

export interface ParsedGeoJson extends GeoJsonInspection {
  readonly data: VectorFeatureCollection;
  readonly warnings: readonly SourceWarning[];
  readonly crs: CrsMetadata;
}

export type ImportedGeoJson = ImportedVectorData;

export type GeoJsonParseResult =
  | { readonly ok: true; readonly value: ParsedGeoJson }
  | { readonly ok: false; readonly error: GeoJsonImportError };

export type GeoJsonFileImportResult =
  | { readonly ok: true; readonly value: ImportedGeoJson }
  | { readonly ok: false; readonly error: GeoJsonImportError };

// JSON.parse needs several times the source byte size in transient strings and
// objects. Keep this deliberately below the Shapefile ceiling until a future
// streaming GeoJSON adapter exists.
export const MAX_GEOJSON_FILE_BYTES = 128 * 1024 * 1024;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const GEOJSON_ROOT_TYPES = new Set([
  'FeatureCollection',
  'Feature',
  'Point',
  'MultiPoint',
  'LineString',
  'MultiLineString',
  'Polygon',
  'MultiPolygon',
  'GeometryCollection',
]);

function canonicalCrs(
  determination: 'declared' | 'format-specification' = 'format-specification',
): CrsMetadata {
  return {
    status: 'identified',
    identifier: CANONICAL_CRS_IDENTIFIER,
    name: 'WGS 84 longitude/latitude',
    determination,
    canonicalIdentifier: CANONICAL_CRS_IDENTIFIER,
    transformation: 'none',
  };
}

function parseJson(
  text: string,
):
  | { readonly ok: true; readonly value: unknown }
  | { readonly ok: false; readonly error: GeoJsonImportError } {
  try {
    return { ok: true, value: JSON.parse(text.replace(/^\uFEFF/, '')) as unknown };
  } catch {
    return {
      ok: false,
      error: { code: 'invalid-json', message: 'This file is not valid JSON.' },
    };
  }
}

function validationError(error: VectorFeatureValidationError): GeoJsonImportError {
  if (error.code === 'unsupported-geometry') {
    return { code: 'unsupported-geometry', message: error.message };
  }
  if (error.code === 'invalid-coordinate' || error.code === 'coordinate-out-of-range') {
    return {
      code: 'invalid-coordinates',
      message:
        error.code === 'coordinate-out-of-range'
          ? 'This GeoJSON contains coordinates outside the WGS84 longitude/latitude range. Check that its declared CRS matches its coordinates.'
          : `This GeoJSON contains coordinates Placeio Maps cannot read. ${error.message}`,
    };
  }
  return {
    code: 'malformed-geojson',
    message: `This GeoJSON has malformed required structure. ${error.message}`,
  };
}

function normalizeGeoJson(
  parsed: unknown,
  crs: CrsMetadata,
  coordinateTransform?: CoordinateTransform,
): GeoJsonParseResult {
  if (
    !isRecord(parsed) ||
    typeof parsed.type !== 'string' ||
    !GEOJSON_ROOT_TYPES.has(parsed.type)
  ) {
    return {
      ok: false,
      error: {
        code: 'not-geojson',
        message: 'This JSON file does not contain a supported GeoJSON object.',
      },
    };
  }

  try {
    const data = parseCanonicalVectorData(parsed, {
      allowBareGeoJson: true,
      ...(coordinateTransform ? { coordinateTransform } : {}),
    });
    const inspection = inspectGeoJson(data);
    return {
      ok: true,
      value: {
        data,
        ...inspection,
        warnings: warningsForInspection(inspection),
        crs,
      },
    };
  } catch (error) {
    if (error instanceof VectorFeatureValidationError) {
      return { ok: false, error: validationError(error) };
    }
    return {
      ok: false,
      error: {
        code: 'invalid-coordinates',
        message: 'Placeio Maps could not safely transform this GeoJSON into WGS84 coordinates.',
      },
    };
  }
}

function crsDeclarationError(parsed: unknown): GeoJsonImportError | ParsedGeoJsonCrs | undefined {
  if (!isRecord(parsed) || !Object.hasOwn(parsed, 'crs')) return undefined;

  const declaration = parseGeoJsonCrsDeclaration(parsed.crs);
  if (declaration.ok) return declaration.value;
  if (declaration.reason === 'unsupported-name' && declaration.detectedName) {
    return {
      code: 'unsupported-crs',
      message: `Placeio Maps found CRS “${declaration.detectedName}” in this GeoJSON, but only supported EPSG identifiers and OGC:CRS84 can be used.`,
    };
  }
  return {
    code: 'unsupported-crs',
    message:
      'This GeoJSON has a malformed CRS declaration. Export it with an EPSG CRS identifier or WGS84 longitude/latitude coordinates.',
  };
}

/**
 * Synchronous RFC 7946 parser used throughout the canonical domain tests. The
 * file-import entry point below additionally resolves projected legacy CRS
 * declarations through the lazily loaded offline EPSG catalogue.
 */
export function parseGeoJsonText(text: string): GeoJsonParseResult {
  const json = parseJson(text);
  if (!json.ok) return json;

  const declaration = crsDeclarationError(json.value);
  if (declaration && !('kind' in declaration)) return { ok: false, error: declaration };
  if (declaration?.kind === 'epsg' && declaration.code !== 4326) {
    return {
      ok: false,
      error: {
        code: 'unsupported-crs',
        message: `Placeio Maps found ${declaration.identifier} in this GeoJSON. Import the file normally so its bundled CRS definition can be loaded.`,
      },
    };
  }

  const crs =
    declaration?.kind === 'epsg'
      ? {
          ...canonicalCrs('declared'),
          identifier: declaration.identifier,
          name: 'WGS 84',
        }
      : canonicalCrs(declaration ? 'declared' : 'format-specification');
  return normalizeGeoJson(json.value, crs);
}

export async function parseGeoJsonTextWithCrs(text: string): Promise<GeoJsonParseResult> {
  const json = parseJson(text);
  if (!json.ok) return json;

  const declaration = crsDeclarationError(json.value);
  if (!declaration) return normalizeGeoJson(json.value, canonicalCrs());
  if (!('kind' in declaration)) return { ok: false, error: declaration };
  if (declaration.kind === 'canonical') {
    return normalizeGeoJson(json.value, canonicalCrs('declared'));
  }

  const { resolveEpsgCatalogueCrs } = await import('../../crs/epsgCatalogue');
  const resolved = await resolveEpsgCatalogueCrs(declaration.code, 'declared');
  if (!resolved.ok) {
    return {
      ok: false,
      error: {
        code: 'unsupported-crs',
        message:
          resolved.error.code === 'not-found'
            ? `Placeio Maps found ${declaration.identifier} in this GeoJSON, but that CRS is not available in the bundled CRS definitions.`
            : `Placeio Maps found ${declaration.identifier} in this GeoJSON, but it cannot be transformed safely with the bundled CRS definitions.`,
      },
    };
  }

  if (resolved.value.metadata.transformation === 'none') {
    return normalizeGeoJson(json.value, resolved.value.metadata);
  }

  const { createWgs84CoordinateTransform } = await import('../../crs/coordinateTransform');
  return normalizeGeoJson(
    json.value,
    resolved.value.metadata,
    createWgs84CoordinateTransform(resolved.value.definition),
  );
}

export function deriveLayerName(fileName: string): string {
  const baseName = fileName.replace(/\.(?:geojson|json)$/i, '');
  const readableName = baseName.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!readableName) return 'Imported GeoJSON';
  return readableName.charAt(0).toUpperCase() + readableName.slice(1);
}

export function hasSupportedGeoJsonExtension(fileName: string): boolean {
  return /\.(?:geojson|json)$/i.test(fileName);
}

export async function importGeoJsonFile(file: File): Promise<GeoJsonFileImportResult> {
  if (!hasSupportedGeoJsonExtension(file.name)) {
    return {
      ok: false,
      error: {
        code: 'unsupported-file-type',
        message: 'Choose a .geojson file, or a .json file containing valid GeoJSON.',
      },
    };
  }

  if (file.size > MAX_GEOJSON_FILE_BYTES) {
    return {
      ok: false,
      error: {
        code: 'file-too-large',
        message:
          'This GeoJSON is too large for the current full-file import. Export a smaller GeoJSON and try again.',
      },
    };
  }

  let text: string;
  try {
    text = await file.text();
  } catch {
    return {
      ok: false,
      error: {
        code: 'file-read',
        message: 'Placeio Maps could not read this file. Check that it is available and try again.',
      },
    };
  }

  const parsed = await parseGeoJsonTextWithCrs(text);
  if (!parsed.ok) return parsed;

  return {
    ok: true,
    value: {
      ...parsed.value,
      suggestedLayerName: deriveLayerName(file.name),
      provenance: {
        originalFileName: file.name,
        originalFormat: 'geojson',
      },
    },
  };
}
